import {
  Control,
  FieldErrors,
  FieldValues,
  UseFormClearErrors,
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormReset,
  UseFormSetValue,
} from 'react-hook-form'
import { EntityLayout, EntityLayoutResponse } from '../types/layout.types'
import {
  DataTypeStore,
  getEntityDataByEntityFilter,
  LayoutResponseType,
  ProgramForFaxReview,
  SearchCriteria,
  WorkflowDefinitionsListFromServiceType,
  WorkflowInstanceType,
} from '@app-store'
import React from 'react'
import { FaxDetailsData } from '../types/intakeQueue.types'
import {
  OnColumnClickCallbackType,
  TableActionType,
  tableFilterType,
} from '@ui-kit'
import { ListingTableProps, ListingTableRow } from '../types/listingTable.types'

export enum SplitType {
  CUSTOM_SPLIT = 'Custom',
  RANGE_SPLIT = 'Range',
}

export type FileType = 'pdf' | 'image' | 'docx' | 'doc' | 'txt' | 'unsupported'

export interface AppComponentProps {
  name?: string
}

export interface CaseDocumentProps {
  caseId?: number | string
  caseDocumentEntityId: number | string
  onSaveClick: () => void
  documentId: number
  onCloseModal: () => void
}

export interface SelectCaseProps {
  intakeType: string
  programName?: ProgramForFaxReview
  programId?: string
  productId?: string
  requestorId?: number
  caseEntityId?: string
  unlockOnAttach: React.MutableRefObject<boolean>
  handleWorkFlowCaseCreate: (instance: WorkflowInstanceType) => void
  entityName?: string
  faxDetails?: FaxDetailsData | null
  updateStatusToCompleted: () => void
  handleShowIntakeLayoutForChildCase: (
    selectedCaseService: CaseIdServiceIdForIntakeType
  ) => void
  updateStatusToProcessing: (id?: number) => void
  maxHeight?: string
  viewEntityRecord?: DataTypeStore
  setViewCaseRecord?: React.Dispatch<React.SetStateAction<boolean>>
  viewCaseRecord?: boolean
  setEditEntityRecord?: React.Dispatch<
    React.SetStateAction<boolean | undefined>
  >
  editEntityRecord?: boolean
}

export interface DocumentItemType {
  docId: number | string
  web_document_status?: {
    value: string
  }
  name?: string
}
export interface CaseLayoutProps {
  name?: string
  control?: Control<FieldValues>
  errors?: FieldErrors<FieldValues>
  clearErrors?: UseFormClearErrors<FieldValues>
  register?: UseFormRegister<FieldValues>
  setValue?: UseFormSetValue<FieldValues>
  data?: EntityLayout[] | null
  loading?: boolean
  caseId?: number
  action?: string
  setAction?: React.Dispatch<React.SetStateAction<string>>
  intakeType: string
  reset?: UseFormReset<FieldValues>
  isInitialSave?: boolean
  setIsInitialSave?: React.Dispatch<React.SetStateAction<boolean>>
  allLayouts?: EntityLayoutResponse | null
  faxDetails: FaxDetailsData | null
}

export interface AcceptConfirmModalType {
  isOpen: boolean
  closeModal: () => void
  deleteMessage?: string
  onYesClick?: () => void
}

export interface FaxHeaderProps {
  intakeType: string
  action: string
  setAction: React.Dispatch<React.SetStateAction<string>>
  setIsDraft: React.Dispatch<React.SetStateAction<boolean>>
  reset: UseFormReset<FieldValues>
  setIsInitialSave: React.Dispatch<React.SetStateAction<boolean>>
  handleSubmit?: UseFormHandleSubmit<FieldValues, undefined>
  handleSave?: (formValues: FieldValues) => Promise<void>
  draftCaseCreate: () => Promise<void>
  unlockOnCreateCase: React.MutableRefObject<boolean>
}

export enum InCallUIView {
  quickConnect = 'quick-connect',
  default = '',
}

export interface DynamicLayoutTableProps {
  layoutId: string
  entityId: string
  tenantId: string
  filterCriteria?: { criteria: SearchCriteria[]; logical_operator: string }
  dashboardId?: string
  isSupervisor?: boolean
  programId: string
  userId: string
  tableHeading: string
  entityName?: string
  refetchKey: number
  queueId: number
  onHyperLinkClick: (queueId: number, name: string, filter?: string) => void
  queueDetails: { type: string; queueId: number }[]
  selectedDashboard?: string
}

export interface ListingDataResponse {
  content: ListingTableRow[]
  totalPages: number
}

export interface ListingColFilter {
  dataType: (typeof tableFilterType)[keyof typeof tableFilterType]
  convertToUppercase: boolean
  dropDownOptions?: {
    label: string
    value: string
  }[]
  format?: string
  onKeyPress?:
    | ((searchCriteria: SearchCriteria) => Promise<string[]>)
    | undefined
}

export interface ListingTableColumn {
  header?: string
  accessor?: string
  type?: string
  componentName?: string
  minWidth?: string
  filterData?: ListingColFilter
  renderComponent?: (
    row: DataTypeStore,
    rowIndex: number,
    onColumnClick: OnColumnClickCallbackType<DataTypeStore>,
    isDisabled: boolean
  ) => JSX.Element
  onClick?: (
    KeyName: keyof ListingTableRow,
    row: ListingTableRow,
    rowIndex: number
  ) => void
}

export interface DashboardListingTableProps {
  entityId: string
  entityName: string
  layout?: LayoutResponseType
  layoutId?: string
  tenantId?: string
  programId?: string
  tableHeading?: string
  userId?: string
  dashboardId: string
  isSupervisor: boolean
  filterCriteria?: { criteria: SearchCriteria[]; logical_operator: string }
  allLayouts?: LayoutResponseType[]
  extraColumns?: ListingTableColumn[]
  extraActions?: (TableActionType<DataTypeStore> & {
    onClick?: (row: ListingTableRow, rowIndex: number) => void
  })[]
  data?: ListingTableRow[]
  showDefaultActions?: ('view' | 'edit' | 'view360')[]
  dataFetchCallback?: (
    payload: getEntityDataByEntityFilter['payload']
  ) => Promise<ListingDataResponse> | ListingDataResponse
  refetchKey?: number
  noDataScreen?: () => JSX.Element
  isLoading?: boolean
  hideFilters?: boolean
  caseEntityId?: string | number
  queueId?: number
  onHyperLinkClick?: (
    queueId: number,
    name: string,
    filter?: string,
    users_id?: string
  ) => void
  queueDetails: { type: string; queueId: number }[]
  selectedDashboard?: string
}

export interface IntakeQueReviewProps {
  name?: string
  entityName?: string
}

export interface CaseIdServiceIdForIntakeType {
  serviceId: number
  caseId: string
  workFlowInstance: WorkflowDefinitionsListFromServiceType
}

export interface RecordType {
  record_status: string
  modified_at?: string | null
  created_at?: string | null
  phone_number: string
  is_preferred: boolean
  id: number
  phone_type: {
    id: number
    record_status: string
    value: string
    category: string
  }
}

export interface DuplicatePopupModalProps {
  isOpen: boolean
  title?: string
  totalResults?: number
  dataFetchCallback?: ListingTableProps['dataFetchCallback']
  entityId: number
  entityName: string
  layout?: LayoutResponseType
  extraColumns?: ListingTableColumn[]
  extraActions?: TableActionType<DataTypeStore>[]
  onProceedAnyway?: () => void
  onCancel: () => void
  isLoading?: boolean
  updateStatusToProcessing?: (id?: number) => void
  communicationId?: string
  faxDetails?: FaxDetailsData | null
  intakeEntityName?: string
  fromInteraction?: boolean
}

export interface BarChartOptionsProps {
  height: number
  categories: string[]
  series: Highcharts.SeriesOptionsType[]
  legendEnabled?: boolean
  xAxisLabelsEnabled?: boolean
  yAxisLabelsEnabled?: boolean
  pointWidth?: number
  onBarClick?: (payload: {
    userId: number
    category: string
    status: string
    count: number
  }) => void
  activePoint?: { category: string; status: string } | null
  highlightedStatus: string | null
}

export interface ColumnChartOptionsProps {
  height: number
  categories: string[]
  series: Highcharts.SeriesOptionsType[]
  xAxisTitle?: string
  yAxisTitle?: string
  onBarClick?: (payload: {
    userId?: number
    category: string
    count: number
  }) => void
}
export interface FilePreviewProps {
  url: string
  fileType: FileType
  setTotalPages?: React.Dispatch<React.SetStateAction<number>>
  docxPreviewUrl?: string
}

export interface ContentTypeProps {
  name?: string
  alias?: string | null
  content_type?: string
}
export const LAYOUT_CONFIG_TYPES = {
  INFLIGHT: 'inflight',
  SHIPMENT: 'shipment',
} as const
export interface LegendItemProps {
  color: string
  label: string
  onClick?: () => void
}

export interface ContentTypeProps {
  name?: string
  alias?: string | null
  content_type?: string
}
export type ModalType =
  | 'PENDING_INTAKE'
  | 'QUICK_DENIAL'
  | 'UNRESOLVED_DEDUPE'
  | null
